import './style.scss';
import '../../universal-notice/v1/scripts';
import { scrollTo } from '../../../js/components/scrollToWithCallback';
import { Media } from '../../../js/utils/const';
import { render, RenderPosition } from '../../../js/utils/render';

const pushAnalytics = (link) => {
  const label = link.dataset.analytics;

  if (label) {
    window.$analytics?.push('header-link-click', { label });
  }
};

const scrollHandler = (link) => {
  const selector = link.getAttribute('href');

  if (!selector) {
    return;
  }

  if (!selector.toString().startsWith('#')) {
    pushAnalytics(link);

    window.open(selector, '_blank');
    return;
  }

  const elementScroll = document.querySelector(selector);

  if (!elementScroll) {
    return;
  }

  const namedElement = elementScroll.closest('[data-block-name]');

  window.programlyScrollingTo = namedElement?.dataset.blockName || 'not-named-block';
  scrollTo(elementScroll, () => window.programlyScrollingTo = null);

  pushAnalytics(link);
};

export default !async function () {
  const header = document.querySelector('.header-v2');
  const headerContainer = header?.querySelector('.header-v2__container');

  let catalogMenu = null;

  if (!header) {
    return;
  }

  const isDesktop = window.matchMedia(`(min-width: ${Media.XL}px)`);
  const catalogButton = header.querySelector('.header-v2__catalog-button');
  const mobileMenuEl = header.querySelector('.ui-header-mobile-menu');
  const mobileMenuTemplate = header.querySelector('#header-mobile-menu');
  const mobileMenuButton = header.querySelector('.header-v2__nav-btn');
  let HeaderMobileMenu = null;

  if (catalogButton) {
    const { UiHeaderDesktopMenu } = await import('@skillbox/front-box/dist/Navigation/UiHeader/components/Menu/UiHeaderDesktopMenu/script');
    const { UiHeaderMobileMenu } = await import('@skillbox/front-box/dist/Navigation/UiHeader/components/Menu/UiHeaderMobileMenu/script');
    const { Slider } = await import('../../../js/components/Slider/Slider');

    const getSuggestions = async ({ search_query, ga_client_id }, { signal }) => {
      /* eslint-disable-next-line */
      const response = await fetch(`${skillsearchApiUrl}_/skillsearch/api/completion/completion/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          search_query,
          ga_client_id
        }),
        signal
      });

      const data = await response.json();

      return { data };
    };

    const headerDesktopMenuTemplate = header.querySelector('#ui-header-desktop-menu');
    let mobileMenuInstance = null;
    let onAfterClose = null;

    const onBeforeClose = () => {
      !onAfterClose && catalogButton.classList.remove('ui-tab--active');
    };

    const onClose = () => {
      catalogMenu = null;

      onAfterClose?.();
      onAfterClose = null;
    };

    const menuOptions = {
      toggleButton: catalogButton,
      slider: Slider,
      onClose,
      onBeforeClose,
      getSuggestions
    };

    const openMenu = async () => {
      !onAfterClose && catalogButton.classList.add('ui-tab--active');

      try {
        if (isDesktop.matches && headerDesktopMenuTemplate) {
          render(header, headerDesktopMenuTemplate.content.cloneNode(true), RenderPosition.AFTERBEGIN);
          catalogMenu = new UiHeaderDesktopMenu(menuOptions);
        } else {
          if (!mobileMenuInstance) {
            mobileMenuInstance = new UiHeaderMobileMenu(menuOptions);
            mobileMenuEl?.addEventListener('scroll', () => headerContainer.classList.toggle('header-v2__container--scrolled', mobileMenuEl.scrollTop > 0));
          }

          catalogMenu = mobileMenuInstance;
        }

        catalogMenu.open();
      } catch (e) {
        console.error('Failed to open catalog menu', e);
        catalogButton.classList.remove('ui-tab--active');
        catalogMenu = null;
        onAfterClose = null;
      }
    };

    const toggleMenu = () => {
      if (catalogMenu) {
        headerContainer.classList.remove('header-v2__container--scrolled');
        catalogMenu.close();

        return;
      }

      window.$mindbox?.push('headerCatalogClick');

      if (!header.classList.contains('header-v2--sticky')) {
        const isSafari = /^(?:(?!chrome|android).)*safari/i.test(navigator.userAgent)

        isSafari
          ? window.scrollTo(0, -1)
          : window.scrollTo(0, 0);
      }

      openMenu();
    };

    isDesktop.addEventListener('change', () => {
      if (catalogMenu) {
        onAfterClose = openMenu;
        catalogMenu.close();
      }
    });

    catalogButton.addEventListener('click', toggleMenu);
  }

  if (mobileMenuTemplate) {
    try {
      ({ HeaderMobileMenu } = await import('../../../js/components/HeaderMobileMenu/scripts'));
    } catch (error) {
      window.Sentry?.captureException('[HeaderMobileMenu]: Failed to load module', error);

      if (mobileMenuButton) {
        mobileMenuButton.setAttribute('hidden', 'hidden');
        mobileMenuButton.setAttribute('aria-hidden', 'true');
      }
    }
  }

  header.addEventListener('click', (evt) => {
    if (mobileMenuButton?.contains(evt.target) && HeaderMobileMenu && mobileMenuTemplate) {
      new HeaderMobileMenu(mobileMenuTemplate, {
        blockName: 'header-v2',
        onAnchorClick: () => catalogMenu?.close()
      }).init();

      window.$analytics?.push('header-burger-click', {
        label: 'Opened'
      });

      return;
    }

    const closestLink = evt.target.closest('.header-v2__nav-link');

    if (closestLink) {
      if (closestLink.getAttribute('href')?.startsWith('#')) {
        evt.preventDefault();
      }

      scrollHandler(closestLink);
    }
  });

  // Fixed header
  const SCROLL_GAP = 28;

  let scrollPos = 0;
  let isHiding = false;
  let directionChangeY = 0;
  let prevScrollRevert = false;

  const setStickyHeader = () => {
    const headerHeight = header.offsetHeight;
    const isScrollRevert = scrollY < scrollPos;

    if (isScrollRevert !== prevScrollRevert) {
      directionChangeY = scrollY;
      prevScrollRevert = isScrollRevert;
    }

    if (Math.abs(scrollY - directionChangeY) < SCROLL_GAP) {
      scrollPos = scrollY;
      return;
    }

    const onHideEnd = () => {
      header.removeEventListener('animationend', onHideEnd);
      header.classList.remove('header-v2--sticky');
      header.style.animation = '';
      isHiding = false;
    };

    if (isScrollRevert && scrollY > (headerHeight * 2) && !header.classList.contains('header-v2--sticky')) {
      requestAnimationFrame(() => {
        header.style.animation = '400ms ease-in-out showHeader';
        header.classList.add('header-v2--sticky');
      })
    } else if (!isScrollRevert && scrollY > headerHeight && header.classList.contains('header-v2--sticky') && !isHiding) {
      isHiding = true;
      header.style.animation = '400ms ease-in-out hideHeader';

      header.addEventListener('animationend', onHideEnd);
      setTimeout(onHideEnd, 400);
    } else if (scrollY < headerHeight) {
      setTimeout(onHideEnd, 400);
    }

    scrollPos = scrollY;
  };

  document.addEventListener('scroll', setStickyHeader);
}();
